
import { AddToCart } from '@/components/AddToCart';
import { useState, useEffect, useMemo } from 'react';
import styles from './ProductInfo.module.css';
import { DesignSelectors } from './Customization/DesignSelectors';
import { PreviewBox } from './Customization/PreviewBox';
import { InitialsInput } from './Customization/InitialsInput';
import { DateInput } from './Customization/DateInput';
import { NamesInput } from './Customization/NamesInput';
import { MessageInput } from './Customization/MessageInput';

import { QuickBuyModal } from './QuickBuyModal';

// Types for broderie options from API
interface BroderieOption {
    type: string;
    enabled: boolean;
    name: string;
    price: number;
    old_price: number | null;
    image: string | null;
}

interface ProductInfoProps {
    product: {
        id: number;
        name: string;
        price: string;
        shortDescription: string;
        stock: number | null;
        inStock?: boolean;
        tabs?: {
            about: string;
            features: string;
            shipping: string;
            contact: string;
        };
        next_product?: {
            id: number;
            slug: string;
            name: string;
            permalink?: string;
        };
        previous_product?: {
            id: number;
            slug: string;
            name: string;
            permalink?: string;
        };
        broderie_options?: BroderieOption[];
    };
}

// Fallback options if API doesn't return broderie_options yet
const FALLBACK_BRODERIE_OPTIONS: BroderieOption[] = [
    { type: 'initiales', enabled: true, name: 'Broderie des initiales', price: 49, old_price: null, image: '/wp-content/uploads/2026/03/initiales-broderie-mariage.webp' },
    { type: 'date', enabled: true, name: 'Broderie de la date du mariage', price: 69, old_price: null, image: '/wp-content/uploads/2026/03/date-broderie-mariage.webp' },
    { type: 'prenoms', enabled: true, name: 'Broderie des prénoms', price: 89, old_price: null, image: '/wp-content/uploads/2026/03/prenoms-broderie-mariage.webp' },
    { type: 'pack', enabled: true, name: 'Pack broderie Prénoms et Date', price: 149, old_price: 158, image: '/wp-content/uploads/2026/03/pack-prenom-date-broderie-mariage.webp' },
    { type: 'message', enabled: true, name: 'Phrase personnalisée', price: 89, old_price: null, image: '/wp-content/uploads/2026/03/phrase-brodee-mariage.webp' },
];

const COLORS = [
    { name: 'Blanc Naturel', hex: '#FDFDFD' },
    { name: 'Rouge', hex: '#E3001B' },
    { name: 'Bordeaux', hex: '#800020' },
    { name: 'Terracotta', hex: '#E2725B' },
    { name: 'Doré', hex: '#D4AF37' },
    { name: 'Jaune', hex: '#FFD300' },
    { name: 'Cuivre', hex: '#B87333' },
    { name: 'Marron glacé', hex: '#8B4513' },
    { name: 'Saumon', hex: '#FF8C69' },
    { name: 'Sable', hex: '#E6D690' },
    { name: 'Vert sapin', hex: '#2E473B' },
    { name: 'Vert pistache', hex: '#B2D3C2' },
    { name: 'Vert d\'eau', hex: '#B8D8D8' },
    { name: 'Bleu ciel', hex: '#87CEEB' },
    { name: 'Bleu roi', hex: '#4169E1' },
    { name: 'Bleu marine', hex: '#000080' },
    { name: 'Rose', hex: '#FFC0CB' },
    { name: 'Lavande', hex: '#E6E6FA' },
    { name: 'Aubergine', hex: '#4B365F' },
    { name: 'Noir', hex: '#000000' },
];

const FONTS = [
    { name: 'Typographie 1', file: 'Brittany_Signature_Demo.otf', family: 'Typo1' },
    { name: 'Typographie 2', file: 'Collection_Free.otf', family: 'Typo2' },
    { name: 'Typographie 3', file: 'Dolce_Vita_Light.ttf', family: 'Typo3' },
    { name: 'Typographie 4', file: 'Funky_Signature.ttf', family: 'Typo4' },
    { name: 'Typographie 5', file: 'Learning_Curve_Dashed.ttf', family: 'Typo5' },
    { name: 'Typographie 6', file: 'Little_days.ttf', family: 'Typo6' },
    { name: 'Typographie 7', file: 'Quicksand_Light.otf', family: 'Typo7' },
    { name: 'Typographie 8', file: 'White_Angelica.ttf', family: 'Typo8' },
    { name: 'Typographie 9', file: 'a_Auto_Signature.otf', family: 'Typo9' },
];

const LOCATIONS = [
    'Côté gauche A',
    'Côté droit E',
    'Fond traîne gauche B',
    'Fond traîne milieu C',
    'Fond traîne droit D',
];

// Per-option state
interface OptionState {
    color: typeof COLORS[0];
    font: typeof FONTS[0];
    location: string;
    initial1: string;
    initial2: string;
    separator: string;
    name1: string;
    name2: string;
    namesSeparator: string;
    dateDay: string;
    dateMonth: string;
    dateYear: string;
    dateFormat: 'numeric' | 'text';
    message: string;
    isColorDropdownOpen: boolean;
    isFontDropdownOpen: boolean;
}

const createDefaultOptionState = (): OptionState => ({
    color: COLORS[0],
    font: FONTS[0],
    location: LOCATIONS[0],
    initial1: '',
    initial2: '',
    separator: 'et',
    name1: '',
    name2: '',
    namesSeparator: 'et',
    dateDay: '',
    dateMonth: '',
    dateYear: '',
    dateFormat: 'numeric',
    message: '',
    isColorDropdownOpen: false,
    isFontDropdownOpen: false,
});

// Calculate message price
const calcMessagePrice = (msg: string): number => {
    const validChars = msg.replace(/[^a-zA-Z0-9àâäéèêëîïôöùûüçÀÂÄÉÈÊËÎÏÔÖÙÛÜÇ]/g, '').length;
    if (validChars <= 0) return 0;
    return 8.17 + ((validChars - 1) * 4.50);
};

// Validation
const isOptionComplete = (type: string, state: OptionState, minPrice: number): boolean => {
    switch (type) {
        case 'initiales':
            return state.initial1.trim() !== '' && state.initial2.trim() !== '';
        case 'date':
            return state.dateDay.trim() !== '' && state.dateMonth.trim() !== '' && state.dateYear.trim() !== '';
        case 'prenoms':
            return state.name1.trim() !== '' && state.name2.trim() !== '';
        case 'pack':
            return state.name1.trim() !== '' && state.name2.trim() !== '' &&
                state.dateDay.trim() !== '' && state.dateMonth.trim() !== '' && state.dateYear.trim() !== '';
        case 'message':
            return state.message.trim() !== '' && calcMessagePrice(state.message) >= minPrice;
        default:
            return true;
    }
};

// Circular progress component for message option
const CircularProgress = ({ current, target }: { current: number; target: number }) => {
    const size = 19;
    const strokeWidth = 2;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const progress = Math.min(1, current / target);
    const offset = circumference - (progress * circumference);

    return (
        <div className={styles.progressCircle}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#e5e5e5"
                    strokeWidth={strokeWidth}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={progress >= 1 ? '#22c55e' : '#171717'}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    style={{ transition: 'stroke-dashoffset 0.3s ease, stroke 0.3s ease' }}
                />
            </svg>
            <span className={styles.progressAmount}>
                {Math.floor(current)}€ / {target}€
            </span>
        </div>
    );
};

export const ProductInfo = ({ product }: ProductInfoProps) => {
    const [activeTab, setActiveTab] = useState<'description' | 'features' | 'shipping' | 'contact'>('description');
    const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
    const [optionStates, setOptionStates] = useState<Record<string, OptionState>>({});

    const [isGuideModalOpen, setIsGuideModalOpen] = useState(false);
    const [isQuickBuyOpen, setQuickBuyOpen] = useState(false);

    // Resolve broderie options: use API data if available, else fallback
    const broderieOptions = useMemo(() => {
        const apiOptions = product.broderie_options;
        if (apiOptions && apiOptions.length > 0) {
            return apiOptions.filter(o => o.enabled);
        }
        return FALLBACK_BRODERIE_OPTIONS;
    }, [product.broderie_options]);

    // Toggle option selection (multi-select)
    const toggleOption = (type: string) => {
        setSelectedOptions(prev => {
            const next = new Set(prev);
            if (next.has(type)) {
                next.delete(type);
            } else {
                next.add(type);
                // Initialize state for this option if not yet
                if (!optionStates[type]) {
                    setOptionStates(s => ({ ...s, [type]: createDefaultOptionState() }));
                }
            }
            return next;
        });
    };

    // Update a specific option's state
    const updateOptionState = (type: string, updates: Partial<OptionState>) => {
        setOptionStates(prev => ({
            ...prev,
            [type]: { ...(prev[type] || createDefaultOptionState()), ...updates },
        }));
    };

    // Lazy load fonts when any font dropdown opens
    const anyFontDropdownOpen = Object.values(optionStates).some(s => s.isFontDropdownOpen);
    useEffect(() => {
        const assetsUrl = (window as any).evcShop?.assetsUrl || '/';
        if (anyFontDropdownOpen) {
            FONTS.forEach(font => {
                const fontUrl = `${assetsUrl}fonts/${font.file}`;
                const fontFace = new FontFace(font.family, `url(${fontUrl})`);
                fontFace.load().then(loadedFace => {
                    document.fonts.add(loadedFace);
                }).catch(err => console.error('Font load failed:', err));
            });
        }
    }, [anyFontDropdownOpen]);

    // Load selected fonts for all active options
    useEffect(() => {
        const assetsUrl = (window as any).evcShop?.assetsUrl || '/';
        selectedOptions.forEach(type => {
            const state = optionStates[type];
            if (!state) return;
            const font = state.font;
            const fontUrl = `${assetsUrl}fonts/${font.file}`;
            const fontFace = new FontFace(font.family, `url(${fontUrl})`);
            fontFace.load().then(loadedFace => {
                document.fonts.add(loadedFace);
            }).catch(err => console.error('Selected font load failed:', err));
        });
    }, [selectedOptions, optionStates]);

    // Calculate option price for a given type
    const getOptionPrice = (type: string, opt: BroderieOption): number => {
        if (type === 'message') {
            const state = optionStates[type];
            return state ? calcMessagePrice(state.message) : 0;
        }
        return opt.price;
    };

    // Parse base price
    const basePrice = useMemo(() => {
        let bp = 0;
        if (typeof product.price === 'number') {
            bp = product.price;
        } else if (typeof product.price === 'string') {
            bp = parseFloat(product.price.replace(/[^\d.,]/g, '').replace(',', '.'));
        }
        return isNaN(bp) ? 0 : bp;
    }, [product.price]);

    // Total options price
    const totalOptionsPrice = useMemo(() => {
        let total = 0;
        selectedOptions.forEach(type => {
            const opt = broderieOptions.find(o => o.type === type);
            if (opt) total += getOptionPrice(type, opt);
        });
        return total;
    }, [selectedOptions, optionStates, broderieOptions]);

    const totalPrice = basePrice + totalOptionsPrice;

    const getDisplayPrice = () => {
        return Math.floor(totalPrice).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    // Validation: all selected options must be complete
    const allOptionsComplete = useMemo(() => {
        if (selectedOptions.size === 0) return true;
        for (const type of selectedOptions) {
            const state = optionStates[type];
            if (!state) return false;
            const opt = broderieOptions.find(o => o.type === type);
            const minPrice = opt?.price || 0;
            if (!isOptionComplete(type, state, minPrice)) return false;
        }
        return true;
    }, [selectedOptions, optionStates, broderieOptions]);

    // Preview text for a given option type
    const getPreviewText = (type: string): string => {
        const state = optionStates[type];
        if (!state) return '';

        switch (type) {
            case 'initiales': {
                const sepDisplay = state.separator === 'vide' ? ' ' : state.separator === 'coeur' ? '♡' : state.separator;
                return `${state.initial1 || 'M'} ${sepDisplay} ${state.initial2 || 'C'}`;
            }
            case 'prenoms': {
                const sepDisplay = state.namesSeparator === 'vide' ? ' ' : state.namesSeparator === 'coeur' ? '♡' : state.namesSeparator;
                return `${state.name1 || 'Emma'} ${sepDisplay} ${state.name2 || 'Thomas'}`;
            }
            case 'date': {
                const d = state.dateDay || '15';
                const m = state.dateMonth || (state.dateFormat === 'numeric' ? '08' : 'août');
                const y = state.dateYear || '2025';
                return state.dateFormat === 'numeric' ? `${d}.${m}.${y}` : `${d} ${m} ${y}`;
            }
            case 'pack': {
                const sepDisplay = state.namesSeparator === 'vide' ? ' ' : state.namesSeparator === 'coeur' ? '♡' : state.namesSeparator;
                const n1 = state.name1 || 'Emma';
                const n2 = state.name2 || 'Thomas';
                const d = state.dateDay || '15';
                const m = state.dateMonth || (state.dateFormat === 'numeric' ? '08' : 'août');
                const y = state.dateYear || '2025';
                const dateText = state.dateFormat === 'numeric' ? `${d}.${m}.${y}` : `${d} ${m} ${y}`;
                return `${n1} ${sepDisplay} ${n2}   ${dateText}`;
            }
            case 'message':
                return state.message || "Je t'aime aujourd'hui et pour toujours.";
            default:
                return '';
        }
    };

    // Build meta for cart
    const buildMeta = (): Record<string, any> | undefined => {
        if (selectedOptions.size === 0) return undefined;

        const options: any[] = [];
        selectedOptions.forEach(type => {
            const opt = broderieOptions.find(o => o.type === type);
            const state = optionStates[type];
            if (!opt || !state) return;

            const optMeta: Record<string, any> = {
                type,
                option_name: opt.name,
                price: getOptionPrice(type, opt),
                color: state.color.name,
                font: state.font.name,
                font_filename: state.font.file,
                location: state.location,
            };

            switch (type) {
                case 'initiales':
                    optMeta.initials = `${state.initial1} ${state.separator} ${state.initial2}`;
                    break;
                case 'prenoms':
                    optMeta.names = `${state.name1} ${state.namesSeparator} ${state.name2}`;
                    break;
                case 'date':
                    optMeta.date = `${state.dateDay} ${state.dateMonth} ${state.dateYear} (${state.dateFormat})`;
                    break;
                case 'pack':
                    optMeta.names = `${state.name1} ${state.namesSeparator} ${state.name2}`;
                    optMeta.date = `${state.dateDay} ${state.dateMonth} ${state.dateYear} (${state.dateFormat})`;
                    break;
                case 'message':
                    optMeta.message = state.message;
                    break;
            }

            options.push(optMeta);
        });

        return {
            options,
            custom_price: totalPrice,
        };
    };

    // Render customization fields for a specific option type
    const renderOptionFields = (type: string) => {
        const state = optionStates[type] || createDefaultOptionState();
        const opt = broderieOptions.find(o => o.type === type);
        const optionComplete = isOptionComplete(type, state, opt?.price || 0);

        return (
            <div key={type} className={styles.multiOptionSection}>
                <h4 className={styles.multiOptionTitle}>{opt?.name || type}</h4>

                {type === 'initiales' && (
                    <InitialsInput
                        initial1={state.initial1}
                        setInitial1={(v) => updateOptionState(type, { initial1: v })}
                        initial2={state.initial2}
                        setInitial2={(v) => updateOptionState(type, { initial2: v })}
                        separator={state.separator}
                        setSeparator={(v) => updateOptionState(type, { separator: v })}
                    />
                )}

                {(type === 'prenoms' || type === 'pack') && (
                    <NamesInput
                        name1={state.name1}
                        setName1={(v) => updateOptionState(type, { name1: v })}
                        name2={state.name2}
                        setName2={(v) => updateOptionState(type, { name2: v })}
                        separator={state.namesSeparator}
                        setSeparator={(v) => updateOptionState(type, { namesSeparator: v })}
                    />
                )}

                {(type === 'date' || type === 'pack') && (
                    <DateInput
                        day={state.dateDay}
                        setDay={(v) => updateOptionState(type, { dateDay: v })}
                        month={state.dateMonth}
                        setMonth={(v) => updateOptionState(type, { dateMonth: v })}
                        year={state.dateYear}
                        setYear={(v) => updateOptionState(type, { dateYear: v })}
                        format={state.dateFormat}
                        setFormat={(newFormat) => updateOptionState(type, { dateFormat: newFormat, dateMonth: '' })}
                        hasTopPadding={type === 'pack'}
                    />
                )}

                {type === 'message' && (
                    <>
                        <MessageInput
                            message={state.message}
                            setMessage={(v) => updateOptionState(type, { message: v })}
                        />
                        <div className={styles.progressWrapper}>
                            <CircularProgress
                                current={calcMessagePrice(state.message)}
                                target={opt?.price || 89}
                            />
                            <span className={styles.progressLabel}>
                                Minimum de {opt?.price || 89}€ de texte personnalisé
                            </span>
                        </div>
                    </>
                )}

                <DesignSelectors
                    selectedColor={state.color}
                    setSelectedColor={(c) => updateOptionState(type, { color: c })}
                    selectedFont={state.font}
                    setSelectedFont={(f) => updateOptionState(type, { font: f })}
                    colors={COLORS}
                    fonts={FONTS}
                    locations={LOCATIONS}
                    selectedLocation={state.location}
                    setSelectedLocation={(l) => updateOptionState(type, { location: l })}
                    isColorDropdownOpen={state.isColorDropdownOpen}
                    setIsColorDropdownOpen={(v) => updateOptionState(type, { isColorDropdownOpen: v })}
                    isFontDropdownOpen={state.isFontDropdownOpen}
                    setIsFontDropdownOpen={(v) => updateOptionState(type, { isFontDropdownOpen: v })}
                    onOpenGuide={() => setIsGuideModalOpen(true)}
                />

                <PreviewBox
                    text={getPreviewText(type)}
                    fontFamily={state.font.family}
                    style={{
                        ...(type === 'date' ? { letterSpacing: '7px' } : {}),
                        ...(type === 'message' ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '1.2' } : {})
                    }}
                />

                {!optionComplete && selectedOptions.has(type) && (
                    <p className={styles.optionIncomplete}>
                        {type === 'message'
                            ? `Veuillez saisir un texte atteignant le minimum de ${opt?.price || 89}€`
                            : 'Veuillez remplir tous les champs pour cette option'}
                    </p>
                )}
            </div>
        );
    };

    return (
        <div className={styles.container}>
            {/* Header with Title */}
            <div className={styles.header}>
                <h1 className={styles.title}>{product.name}</h1>
                {/* Desktop Nav */}
                <div className={`${styles.productNav} ${styles.desktopNav}`}>
                    {product.previous_product ? (
                        <a href={product.previous_product.permalink || `/produit/${product.previous_product.slug}`} className={styles.navLink} title={product.previous_product.name}>
                            <span className={styles.navIcon}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            Précédent
                        </a>
                    ) : (
                        <span className={`${styles.navLink} ${styles.disabled}`} style={{ opacity: 0.3, cursor: 'default' }}>
                            <span className={styles.navIcon}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                            Précédent
                        </span>
                    )}

                    <span className={styles.navDivider}>|</span>

                    {product.next_product ? (
                        <a href={product.next_product.permalink || `/produit/${product.next_product.slug}`} className={styles.navLink} title={product.next_product.name}>
                            Suivant
                            <span className={styles.navIcon}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </a>
                    ) : (
                        <span className={`${styles.navLink} ${styles.disabled}`} style={{ opacity: 0.3, cursor: 'default' }}>
                            Suivant
                            <span className={styles.navIcon}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </span>
                    )}
                </div>
            </div>

            <p className={styles.price}>{getDisplayPrice()}</p>

            {/* Short Description */}
            <div
                className={styles.shortDescription}
                dangerouslySetInnerHTML={{ __html: product.shortDescription }}
            />

            {/* Mobile Nav */}
            <div className={`${styles.productNav} ${styles.mobileNav}`}>
                {product.previous_product ? (
                    <a href={product.previous_product.permalink || `/produit/${product.previous_product.slug}`} className={styles.navLink} title={product.previous_product.name}>
                        <span className={styles.navIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        Précédent
                    </a>
                ) : (
                    <span className={`${styles.navLink} ${styles.disabled}`} style={{ opacity: 0.3, cursor: 'default' }}>
                        <span className={styles.navIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                        Précédent
                    </span>
                )}
                <span className={styles.navDivider}>|</span>
                {product.next_product ? (
                    <a href={product.next_product.permalink || `/produit/${product.next_product.slug}`} className={styles.navLink} title={product.next_product.name}>
                        Suivant
                        <span className={styles.navIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </a>
                ) : (
                    <span className={`${styles.navLink} ${styles.disabled}`} style={{ opacity: 0.3, cursor: 'default' }}>
                        Suivant
                        <span className={styles.navIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </span>
                    </span>
                )}
            </div>

            {/* Embroidery Options - Multi-select */}
            <h3 className={styles.optionsTitle}>Personnaliser votre voile ?</h3>
            <div className={styles.optionsWrapper}>
                <div className={styles.optionsSection}>
                    {broderieOptions.map((option) => (
                        <div
                            key={option.type}
                            className={`${styles.optionCard} ${selectedOptions.has(option.type) ? styles.optionSelected : ''}`}
                            onClick={() => toggleOption(option.type)}
                        >
                            <div className={styles.optionImageWrapper}>
                                {option.image && <img src={option.image} alt={option.name} className={styles.optionImage} />}
                            </div>
                            <div className={styles.optionDetails}>
                                <h3 className={styles.optionName}>{option.name}</h3>
                                <span className={styles.optionPrice}>
                                    {option.old_price && (
                                        <span className={styles.oldPrice}>{option.old_price}€ </span>
                                    )}
                                    {option.type === 'message' ? `à partir de ${option.price}€` : `${option.price}€`}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Customization Fields for each selected option */}
            {selectedOptions.size > 0 && (
                <div className={styles.customizationContainer}>
                    {Array.from(selectedOptions).map(type => renderOptionFields(type))}
                </div>
            )}


            {/* Tabs */}
            <div className={styles.tabsContainer}>
                <div className={styles.tabHeaders}>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'description' ? styles.active : ''}`}
                        onClick={() => setActiveTab('description')}
                    >
                        À propos
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'features' ? styles.active : ''}`}
                        onClick={() => setActiveTab('features')}
                    >
                        Caractéristiques
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'shipping' ? styles.active : ''}`}
                        onClick={() => setActiveTab('shipping')}
                    >
                        Livraisons et retours
                    </button>
                    <button
                        className={`${styles.tabButton} ${activeTab === 'contact' ? styles.active : ''}`}
                        onClick={() => setActiveTab('contact')}
                    >
                        Contact
                    </button>
                </div>
                <div className={styles.tabContent}>
                    {activeTab === 'description' && (
                        <div dangerouslySetInnerHTML={{ __html: product.tabs?.about || product.shortDescription }} />
                    )}
                    {activeTab === 'features' && (
                        <div dangerouslySetInnerHTML={{ __html: product.tabs?.features || '<p>Caractéristiques du produit (à remplir)</p>' }} />
                    )}
                    {activeTab === 'shipping' && (
                        <div dangerouslySetInnerHTML={{ __html: product.tabs?.shipping || '<p>Informations sur la livraison...</p>' }} />
                    )}
                    {activeTab === 'contact' && (
                        <div dangerouslySetInnerHTML={{ __html: product.tabs?.contact || '<p>Formulaire de contact...</p>' }} />
                    )}
                </div>
            </div>



            {/* Add To Cart & Payment */}
            <div className={styles.actions}>
                <div className={styles.buttonsRow}>
                    <AddToCart
                        productId={product.id}
                        stock={product.stock}
                        inStock={product.inStock}
                        fullWidth={false}
                        showQuantity={false}
                        disabled={!allOptionsComplete}
                        meta={buildMeta()}
                    />

                    {/* Calculate price for Apple Pay independent of meta logic return */}
                    {(() => {
                        const isOutOfStock = product.inStock !== undefined ? !product.inStock : (product.stock !== null && product.stock <= 0);
                        if (!(window as any).evcShop?.stripeKey || isOutOfStock) {
                            return null;
                        }

                        return (
                            <>
                                <button
                                    className={`${styles.applePayButton} ${styles.applePayButtonFake}`}
                                    onClick={() => setQuickBuyOpen(true)}
                                    disabled={!allOptionsComplete}
                                >
                                    <svg className={styles.applePayIcon} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 814 1000"><path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57.8-155.5-127.4c-58.3-81.1-105.2-207.4-105.2-328.2 0-192.8 125.3-295.2 248.5-295.2 65.6 0 120.3 43.1 161.5 43.1 39.1 0 100.2-45.8 174.5-45.8 28.2 0 129.5 2.5 196.3 95.5zM554.1 159.4c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.2 32.4-55.8 83.6-55.8 135.4 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 136.2-71.2z" fill="currentColor"/></svg>
                                    <span className={styles.applePayText}>Pay</span>
                                </button>

                                <QuickBuyModal
                                    isOpen={isQuickBuyOpen}
                                    onClose={() => setQuickBuyOpen(false)}
                                    product={product}
                                    options={
                                        (() => {
                                            const opts: Array<{ label: string; value: string }> = [];
                                            selectedOptions.forEach(type => {
                                                const opt = broderieOptions.find(o => o.type === type);
                                                const state = optionStates[type];
                                                if (!opt || !state) return;
                                                opts.push({ label: 'Option', value: opt.name });
                                                opts.push({ label: 'Couleur', value: state.color.name });

                                                if (type === 'initiales') {
                                                    opts.push({ label: 'Initiales', value: `${state.initial1} ${state.separator} ${state.initial2}` });
                                                } else if (type === 'prenoms') {
                                                    opts.push({ label: 'Prénoms', value: `${state.name1} ${state.namesSeparator} ${state.name2}` });
                                                } else if (type === 'date' || type === 'pack') {
                                                    const dateText = state.dateFormat === 'numeric' ? `${state.dateDay}.${state.dateMonth}.${state.dateYear}` : `${state.dateDay} ${state.dateMonth} ${state.dateYear}`;
                                                    opts.push({ label: 'Date', value: dateText });
                                                    if (type === 'pack') {
                                                        opts.push({ label: 'Prénoms', value: `${state.name1} ${state.namesSeparator} ${state.name2}` });
                                                    }
                                                } else if (type === 'message') {
                                                    opts.push({ label: 'Message', value: state.message });
                                                }
                                                opts.push({ label: 'Typographie', value: state.font.name });
                                                opts.push({ label: 'Emplacement', value: state.location });
                                            });
                                            return opts;
                                        })()
                                    }
                                    totalPrice={totalPrice}
                                    onPaymentSuccess={() => {
                                        setQuickBuyOpen(false);
                                        window.location.href = '/commande-confirmee';
                                    }}
                                />
                            </>
                        );
                    })()}
                </div>
            </div>

            {/* Guide Modal */}
            {isGuideModalOpen && (
                <div className={styles.modalOverlay} onClick={() => setIsGuideModalOpen(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <button className={styles.closeButton} onClick={() => setIsGuideModalOpen(false)}>×</button>
                        <h3 className={styles.modalTitle}>Guide des emplacements de la broderie</h3>
                        <img
                            src="/wp-content/uploads/2026/01/guide-emplacement-voile.webp"
                            alt="Guide emplacement broderie"
                            className={styles.modalImage}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
